import {
  ArrowTrendingUpIcon,
  BeakerIcon,
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline'
import { useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { LinkedInIcon } from '@duck/ui'
import { useAuth } from '@/hooks/useAuth'
import { useTar } from '@/modules'
import { trackMarketing } from '@/modules/marketingAnalytics'
import { Logo } from '../Misc/Logo'
import { Manifesto } from './Manifesto'
import { ExternalAssets } from '@/types/externalURLs'

const HOW_IT_WORKS_ITEMS = [
  {
    icon: BeakerIcon,
    titleKey: 'about.howItWorksItem1Title',
    descriptionKey: 'about.howItWorksItem1Description',
  },
  {
    icon: ChatBubbleLeftRightIcon,
    titleKey: 'about.howItWorksItem2Title',
    descriptionKey: 'about.howItWorksItem2Description',
  },
  {
    icon: ArrowTrendingUpIcon,
    titleKey: 'about.howItWorksItem3Title',
    descriptionKey: 'about.howItWorksItem3Description',
  },
] as const

const TEAM_LINKS = [
  {
    nameKey: 'about.teamMemberTalName',
    href: 'https://www.linkedin.com/in/talnmizrachi/',
    avatarSrc: ExternalAssets.FounderTal,
    person: 'tal',
  },
  {
    nameKey: 'about.teamMemberBenName',
    href: 'https://www.linkedin.com/in/benharan/',
    avatarSrc: ExternalAssets.FounderBen,
    person: 'ben',
  },
] as const

export const AboutPage = () => {
  const { t } = useTar()
  const { isAuthenticated, openSignupDialog } = useAuth()
  const navigate = useNavigate()

  const handlePrimaryCta = () => {
    if (isAuthenticated) {
      trackMarketing('about:manifesto:primary_cta_click', { variant: 'try_challenge' })
      navigate({ to: '/challenges/available' })
    } else {
      trackMarketing('about:manifesto:primary_cta_click', { variant: 'signup' })
      openSignupDialog()
    }
  }

  useEffect(() => {
    const applyHashScroll = () => {
      const hash = decodeURIComponent(window.location.hash.replace(/^#/, '').trim())
      if (!hash) return
      requestAnimationFrame(() => {
        document.getElementById(hash)?.scrollIntoView({ block: 'start' })
      })
    }
    applyHashScroll()
    window.addEventListener('hashchange', applyHashScroll)
    return () => window.removeEventListener('hashchange', applyHashScroll)
  }, [])

  return (
    <div className="flex flex-1 w-full justify-center">
      <div className="flex-1 min-w-0 max-w-6xl space-y-10 md:space-y-12 mt-4 md:mt-8 mb-16">
        <section
          className="grid grid-cols-1 md:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] items-start gap-10 px-6 py-8 md:px-10 md:py-12"
          aria-labelledby="about-heading"
        >
          <div className="flex flex-col items-start gap-6 max-w-xl">
            <h1
              id="about-heading"
              className="flex items-baseline flex-wrap gap-x-3 text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-foreground"
            >
              <span>{t('common.about')}</span>
              <Logo />
              <span className="text-accent">.</span>
            </h1>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
              {t('about.subtitle')}
            </p>
            <p className="text-base md:text-lg text-foreground/90 leading-relaxed">
              {t('about.missionP1')}
            </p>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
              <strong className="text-foreground">XP Lab</strong> {t('about.missionP2Suffix')}
            </p>
          </div>

          <aside className="rounded-xl border border-border bg-card/60 p-6 md:mt-8">
            <p className="mb-5 text-center text-[11px] font-semibold uppercase tracking-[0.24em] text-accent">
              {t('about.theTeam')}
            </p>
            <div className="flex flex-wrap items-start justify-center gap-x-3 gap-y-5">
              {TEAM_LINKS.map((person) => {
                const name = t(person.nameKey)
                const handleClick = () =>
                  trackMarketing('about:team:linkedin_click', { person: person.person })
                return (
                  <div key={person.href} className="flex w-[6.75rem] flex-col items-center gap-3">
                    <a
                      href={person.href}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={t('about.linkedInProfileAriaLabel', { name })}
                      onClick={handleClick}
                      className="group relative block h-24 w-24 rounded-full transition-all duration-300 hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                    >
                      <span className="block h-full w-full overflow-hidden rounded-full border border-accent/25 bg-accent/10 shadow-[0_18px_50px_-28px_hsl(var(--color-accent)/0.9)] transition-all duration-300 group-hover:border-accent/60 group-hover:shadow-[0_24px_60px_-24px_hsl(var(--color-accent)/0.9)]">
                        <img
                          src={person.avatarSrc}
                          alt=""
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                      </span>
                      <span className="absolute -bottom-1 -end-1 flex h-8 w-8 items-center justify-center rounded-full border-2 border-card bg-card text-accent shadow-lg transition-transform duration-300 group-hover:scale-105">
                        <LinkedInIcon size={16} />
                      </span>
                    </a>
                    <a
                      href={person.href}
                      target="_blank"
                      rel="noreferrer"
                      onClick={handleClick}
                      className="text-center text-sm font-semibold text-foreground transition-colors hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                    >
                      {name}
                    </a>
                  </div>
                )
              })}
            </div>
          </aside>
        </section>

        <section className="space-y-8" aria-labelledby="how-it-works-heading">
          <div className="space-y-3">
            <h2
              id="how-it-works-heading"
              className="text-3xl font-bold text-foreground tracking-tight"
            >
              {t('about.howItWorksTitle')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3">
            {HOW_IT_WORKS_ITEMS.map((item) => {
              const Icon = item.icon
              return (
                <div
                  key={item.titleKey}
                  className="px-6 py-5 md:py-2 md:border-s md:border-border/50 md:first:border-s-0 space-y-3"
                >
                  <h3 className="flex items-baseline gap-2 text-lg font-semibold text-foreground">
                    <Icon aria-hidden className="h-4 w-4 self-center text-accent/70" />
                    {t(item.titleKey)}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed text-sm">
                    {t(item.descriptionKey)}
                  </p>
                </div>
              )
            })}
          </div>
        </section>

        <Manifesto onPrimaryCta={handlePrimaryCta} />

        <section
          className="relative isolate border-y border-border py-14 md:py-20"
          aria-labelledby="contact-heading"
        >
          <EnvelopeIcon
            aria-hidden
            className="pointer-events-none absolute -end-12 -top-10 h-72 w-72 text-accent/[0.13] -rotate-6 select-none"
          />
          <div className="relative space-y-6 max-w-2xl">
            <h2
              id="contact-heading"
              className="text-3xl md:text-4xl font-bold tracking-tight text-foreground"
            >
              {t('about.getInTouch')}
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed">
              {t('about.getInTouchDescription')}
            </p>
            <a
              href="mailto:tal@xp-lab.com"
              onClick={() => trackMarketing('about:contact:email_click')}
              className="group inline-flex items-center gap-2 rounded-lg bg-accent hover:bg-accent/85 text-accent-foreground text-base font-semibold px-5 py-3 shadow-[0_0_40px_-8px_hsl(var(--color-accent)/0.55)] transition-all hover:-translate-y-0.5"
            >
              <EnvelopeIcon aria-hidden className="h-5 w-5" />
              {t('about.contactUs')}
            </a>
          </div>
        </section>

      </div>
    </div>
  )
}
